import {
  OrgSiteListEntry,
  Organisation,
} from "@inrange/building-manager-api-client/models-organisation";
import {
  SiteScore,
  calcBehindTheMeter,
  calcCfeScore,
  calcInRangeNetwork,
  getScoreBySiteID,
  inrangeScoreStatusColor,
  siteStatusMap,
} from "@inrange/theme-components";
import React from "react";
import styled from "styled-components";
import { formatCurrencyToNearestTen } from "../../formatting/formatCurrency";
import { formatUnitsToNearestTen } from "../../formatting/formatKiloUnits";
import formatMass from "../../formatting/formatMass";
import GenerationHeadroomCell from "./GenerationHeadroomCell";
import SiteLink from "./SiteLink";
import SiteNameAddressData from "./SiteNameAddressData";
import TableCellData from "./TableCellData";

const SiteRow: React.FC<{
  site: OrgSiteListEntry;
  organisation: Organisation;
  displayInRangeScoreCol: boolean;
  allSitesNotStarted: boolean;
  hideSavings?: boolean;
}> = ({ site, organisation, displayInRangeScoreCol, allSitesNotStarted, hideSavings }) => {
  const energyProcured = site.energyFlowAnnual.procurement;
  const behindTheMeter = calcBehindTheMeter(site.energyFlowAnnual) * 100;
  const inRangeNetwork = calcInRangeNetwork(site.energyFlowAnnual) * 100;
  const carbonEmissions = site.calculations.emissionsAvoided.totalAvoidance;
  const carbonFreeEnergy = calcCfeScore(site.energyFlowAnnual) * 100;
  const ppaSavings = site.financialModels.procurement.savingsPerPpa.reduce(
    (acc, ppa) => acc + ppa,
    0
  );

  return (
    <SiteRowView
      site={site}
      organisation={organisation}
      ppaSavings={ppaSavings}
      energyProcured={energyProcured}
      behindTheMeter={behindTheMeter}
      inRangeNetwork={inRangeNetwork}
      carbonEmissions={carbonEmissions}
      carbonFreeEnergy={carbonFreeEnergy}
      displayInRangeScoreCol={displayInRangeScoreCol}
      allSitesNotStarted={allSitesNotStarted}
      orgID={organisation.id}
      hideSavings={hideSavings}
    />
  );
};

interface SiteRowViewProps {
  site: OrgSiteListEntry;
  organisation: Organisation;
  ppaSavings: number;
  energyProcured: number;
  behindTheMeter: number;
  inRangeNetwork: number;
  carbonEmissions: number;
  carbonFreeEnergy: number;
  displayInRangeScoreCol: boolean;
  allSitesNotStarted: boolean;
  orgID: string;
  hideSavings?: boolean;
}

const SiteRowView: React.FC<SiteRowViewProps> = ({
  site,
  organisation,
  ppaSavings,
  energyProcured,
  behindTheMeter,
  inRangeNetwork,
  carbonEmissions,
  carbonFreeEnergy,
  displayInRangeScoreCol,
  allSitesNotStarted,
  orgID,
  hideSavings,
}) => {
  const score = getScoreBySiteID(orgID, site);

  if (displayInRangeScoreCol) {
    return (
      <TenantSiteRowViewInrangeScoreTrue
        site={site}
        organisation={organisation}
        ppaSavings={ppaSavings}
        energyProcured={energyProcured}
        carbonEmissions={carbonEmissions}
        carbonFreeEnergy={carbonFreeEnergy}
        score={score}
        hideSavings={hideSavings}
      />
    );
  }
  return (
    <TenantSiteRowViewInrangeScoreFalse
      site={site}
      organisation={organisation}
      ppaSavings={ppaSavings}
      energyProcured={energyProcured}
      behindTheMeter={behindTheMeter}
      inRangeNetwork={inRangeNetwork}
      carbonEmissions={carbonEmissions}
      carbonFreeEnergy={carbonFreeEnergy}
      allSitesNotStarted={allSitesNotStarted}
      hideSavings={hideSavings}
    />
  );
};

export default SiteRow;

interface LandlordTableCellProps {
  site: OrgSiteListEntry;
  organisation: Organisation;
}

const LandlordTableCell: React.FC<LandlordTableCellProps> = ({
  site,
  organisation,
}) => {
  const landlords: JSX.Element[] = [];
  for (const siteOwnership of site.siteOwnerships) {
    if (siteOwnership.isTest) {
      continue; // Skip test ownerships
    }
    if (siteOwnership.ownership === "landlord") {
      landlords.push(<p key={siteOwnership.orgID}>{siteOwnership.name}</p>);
    }
    if (
      siteOwnership.ownership === "ownerOccupier" &&
      siteOwnership.orgID === organisation.id
    ) {
      landlords.push(
        <p key={siteOwnership.orgID}>
          {organisation.name} <br />
          <i>(Owner Occupier)</i>
        </p>
      );
    }
  }
  return (
    <td>
      {landlords.map((landlord) => (
        <div key={landlord.key}>{landlord}</div>
      ))}
    </td>
  );
};

interface TenantSiteRowViewInrangeScoreTrueProps {
  site: OrgSiteListEntry;
  organisation: Organisation;
  ppaSavings: number;
  energyProcured: number;
  carbonEmissions: number;
  carbonFreeEnergy: number;
  score?: number;
  hideSavings?: boolean;
}

const TenantSiteRowViewInrangeScoreTrue: React.FC<
  TenantSiteRowViewInrangeScoreTrueProps
> = ({
  site,
  organisation,
  ppaSavings,
  energyProcured,
  carbonEmissions,
  carbonFreeEnergy,
  score,
  hideSavings,
}) => {
  return (
    <tr key={site.id} id={site.id}>
      <td style={{ width: "170px" }}>
        {site.operationalStatus === "notStarted" ? (
          <SiteScore
            siteScore={score}
            statusColor={
              score !== undefined ? inrangeScoreStatusColor[score] : undefined
            }
          />
        ) : (
          <StatusContainer>
            {siteStatusMap[site.operationalStatus].map((status, i) => {
              return <span key={i}>{status}</span>;
            })}
          </StatusContainer>
        )}
      </td>
      <td>
        <TableCellData>
          <SiteNameAddressData
            siteName={site.name}
            siteAddress={site.address}
            siteId={site.id}
            orgId={organisation.id}
          />
        </TableCellData>
      </td>
      {!hideSavings && (
        <td>
          {formatCurrencyToNearestTen(ppaSavings, site.currencyCode, 0, false)}
        </td>
      )}
      <td>{formatUnitsToNearestTen(energyProcured, "kWh")}</td>
      <LandlordTableCell site={site} organisation={organisation} />
      <td>
        <TableCellData>{formatMass(carbonEmissions)}</TableCellData>
        <TableCellData>{carbonFreeEnergy.toFixed(1)}% CFE</TableCellData>
      </td>
      <td style={{ width: "170px" }}>
        <SiteLink siteId={site.id} orgId={organisation.id} />
      </td>
    </tr>
  );
};

interface TenantSiteRowViewInrangeScoreFalseProps {
  site: OrgSiteListEntry;
  organisation: Organisation;
  ppaSavings: number;
  energyProcured: number;
  behindTheMeter: number;
  inRangeNetwork: number;
  carbonEmissions: number;
  carbonFreeEnergy: number;
  allSitesNotStarted: boolean;
  hideSavings?: boolean;
}

const TenantSiteRowViewInrangeScoreFalse: React.FC<
  TenantSiteRowViewInrangeScoreFalseProps
> = ({
  site,
  organisation,
  ppaSavings,
  energyProcured,
  behindTheMeter,
  inRangeNetwork,
  carbonEmissions,
  carbonFreeEnergy,
  allSitesNotStarted,
  hideSavings,
}) => {
  return (
    <tr key={site.id} id={site.id}>
      {!allSitesNotStarted && (
        <td style={{ width: "170px" }}>
          <StatusContainer>
            {site.operationalStatus === "notStarted"
              ? "Feasibility study"
              : siteStatusMap[site.operationalStatus].map((status, i) => {
                  return <span key={i}>{status}</span>;
                })}
          </StatusContainer>
        </td>
      )}
      <td>
        <TableCellData>
          <SiteNameAddressData
            siteName={site.name}
            siteAddress={site.address}
            siteId={site.id}
            orgId={organisation.id}
          />
        </TableCellData>
      </td>
      <td>
        <GenerationHeadroomCell site={site} />
      </td>
      {!hideSavings && (
        <td>
          {formatCurrencyToNearestTen(ppaSavings, site.currencyCode, 0, false)}
        </td>
      )}
      <td>{formatUnitsToNearestTen(energyProcured, "kWh")}</td>
      <td>
        <TableCellData>
          Behind the Meter: {behindTheMeter.toFixed(1)}%
        </TableCellData>
        <TableCellData>
          InRange Network: {inRangeNetwork.toFixed(1)}%
        </TableCellData>
      </td>
      <td>
        <TableCellData>{formatMass(carbonEmissions)}</TableCellData>
        <TableCellData>{carbonFreeEnergy.toFixed(1)}% CFE</TableCellData>
      </td>
      <td style={{ width: "170px" }}>
        <SiteLink siteId={site.id} orgId={organisation.id} />
      </td>
    </tr>
  );
};

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
`;
